import type { VoyageReportKind } from './schemas.js';

/**
 * Sefer olayları (ADR 0082 Ek §1; ortağın `sefer-2.jpeg` S4): EOSP, COSP, Pilot On/Off, NOR,
 * First Line, All Fast gibi olaylar raporların alanlarıdır; kayıt modeli değişmez. Bu modül olay
 * listesini raporlardan türetir: Recent Events, Voyage Timeline ve Event Types (Reference Data)
 * aynı kataloğu okur. Saklanmaz; rapor düzeltilince olay da değişir (S11, S12).
 */

export const EVENT_CATEGORIES = ['navigation', 'port', 'anchorage', 'commercial'] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

/** Olay türleri, operasyon akışı sırasıyla (eşit zamanda sıra da budur). */
export const EVENT_TYPES = [
  'pilot_on',
  'cast_off',
  'pilot_off',
  'cosp',
  'noon_position',
  'strait_entry',
  'strait_exit',
  'canal_entry',
  'canal_exit',
  'waiting_start',
  'waiting_end',
  'drift_start',
  'drift_end',
  'anchored',
  'anchor_aweigh',
  'eosp',
  'nor_tendered',
  'nor_accepted',
  'first_line',
  'all_fast',
  'laytime_commenced',
  'cargo_start',
  'cargo_complete',
  'on_hire',
  'off_hire',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/**
 * Olayın kaynağı: rapor türü ve alan. `reportedAt` rapor zamanıdır; diğerleri `data` içindeki
 * yoldur (`nor.tenderedAt`). `schema` verilirse yalnız o şema sürümünde (Departure; ADR 0058 §1).
 */
export interface EventSource {
  kind: VoyageReportKind;
  field: string;
  schema?: 1 | 2;
}

export interface EventTypeDef {
  type: EventType;
  category: EventCategory;
  sources: readonly EventSource[];
}

const NOR_KINDS = ['eosp', 'anchorage_start', 'arrival'] as const;
const at = (kind: VoyageReportKind, schema?: 1 | 2): EventSource =>
  schema ? { kind, field: 'reportedAt', schema } : { kind, field: 'reportedAt' };
const field = (kind: VoyageReportKind, path: string, schema?: 1 | 2): EventSource =>
  schema ? { kind, field: path, schema } : { kind, field: path };

/**
 * Katalog (Event Types sayfası bunu gösterir). Departure sürüm 2'de rapor zamanı Cast Off'tur,
 * sürüm 1'de pilot ayrılışı (`letGo` son halat; ADR 0058 §1). Berthing'de rapor zamanı yanaşmadır:
 * All Fast alanı boşsa rapor zamanı All Fast sayılır.
 */
export const EVENT_TYPE_DEFS: readonly EventTypeDef[] = [
  {
    type: 'pilot_on',
    category: 'port',
    sources: [field('departure', 'pilotOnBoard'), field('arrival', 'pilotOnBoard')],
  },
  {
    type: 'cast_off',
    category: 'port',
    sources: [at('departure', 2), field('departure', 'letGo', 1)],
  },
  {
    type: 'pilot_off',
    category: 'port',
    sources: [field('departure', 'pilotOff', 2), at('departure', 1), field('arrival', 'pilotOff')],
  },
  { type: 'cosp', category: 'navigation', sources: [at('cosp')] },
  { type: 'noon_position', category: 'navigation', sources: [at('noon')] },
  { type: 'strait_entry', category: 'navigation', sources: [at('strait_entry')] },
  { type: 'strait_exit', category: 'navigation', sources: [at('strait_exit')] },
  { type: 'canal_entry', category: 'navigation', sources: [at('canal_entry')] },
  { type: 'canal_exit', category: 'navigation', sources: [at('canal_exit')] },
  { type: 'waiting_start', category: 'anchorage', sources: [at('waiting_start')] },
  { type: 'waiting_end', category: 'anchorage', sources: [at('waiting_end')] },
  { type: 'drift_start', category: 'anchorage', sources: [at('drift_start')] },
  { type: 'drift_end', category: 'anchorage', sources: [at('drift_end')] },
  { type: 'anchored', category: 'anchorage', sources: [at('anchorage_start')] },
  { type: 'anchor_aweigh', category: 'anchorage', sources: [at('anchorage_end')] },
  { type: 'eosp', category: 'navigation', sources: [at('eosp')] },
  {
    type: 'nor_tendered',
    category: 'port',
    sources: NOR_KINDS.map((kind) => field(kind, 'nor.tenderedAt')),
  },
  {
    type: 'nor_accepted',
    category: 'port',
    sources: NOR_KINDS.map((kind) => field(kind, 'nor.acceptedAt')),
  },
  { type: 'first_line', category: 'port', sources: [field('arrival', 'firstLine')] },
  {
    type: 'all_fast',
    category: 'port',
    sources: [field('arrival', 'allFast'), at('arrival')],
  },
  {
    type: 'laytime_commenced',
    category: 'port',
    sources: NOR_KINDS.map((kind) => field(kind, 'nor.laytimeCommencedAt')),
  },
  { type: 'cargo_start', category: 'port', sources: [at('cargo_start')] },
  { type: 'cargo_complete', category: 'port', sources: [at('cargo_complete')] },
  { type: 'on_hire', category: 'commercial', sources: [at('on_hire')] },
  { type: 'off_hire', category: 'commercial', sources: [at('off_hire')] },
];

const CATEGORY = new Map(EVENT_TYPE_DEFS.map((def) => [def.type, def.category]));
const ORDER = new Map(EVENT_TYPES.map((type, index) => [type, index]));

/** Olayın okuduğu rapor alanları; `schema` yoksa güncel sürüm (2). */
export interface EventReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  schema?: number;
  status: string;
  data: Record<string, unknown>;
}

export interface VoyageEvent {
  /** `<raporId>:<tür>`; listede tektir. */
  key: string;
  type: EventType;
  category: EventCategory;
  /** UTC ISO-8601. */
  at: string;
  reportId: string;
  kind: VoyageReportKind;
}

const instant = (value: unknown): string | null =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null;

function read(report: EventReport, path: string): unknown {
  if (path === 'reportedAt') return report.reportedAt;
  let value: unknown = report.data;
  for (const part of path.split('.')) {
    value =
      value !== null && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)[part]
        : undefined;
  }
  return value;
}

/**
 * Raporların olayları, zamana göre (eşitlikte akış sırası). Yalnız gönderilmiş raporlar okunur:
 * özet ve performans gibi (ADR 0022 §4). Aynı tür aynı anda iki raporda varsa (EOSP ve Berthing'deki
 * aynı NOR) bir kez sayılır. Bozuk ya da boş alan atlanır, hata vermez. Bir türün kaynaklarından
 * ilk dolu olan alınır (All Fast boşsa Berthing'in rapor zamanı).
 */
export function reportEvents(reports: readonly EventReport[]): VoyageEvent[] {
  const events: VoyageEvent[] = [];
  const seen = new Set<string>();
  const sorted = [...reports].sort(
    (a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt) || a.id.localeCompare(b.id),
  );
  for (const report of sorted) {
    if (report.status !== 'submitted') continue;
    const schema = report.schema === 1 ? 1 : 2;
    for (const def of EVENT_TYPE_DEFS) {
      const sources = def.sources.filter(
        (s) => s.kind === report.kind && (s.schema === undefined || s.schema === schema),
      );
      const found = sources.map((s) => instant(read(report, s.field))).find((v) => v !== null);
      if (!found) continue;
      const once = `${def.type}@${Date.parse(found)}`;
      if (seen.has(once)) continue;
      seen.add(once);
      events.push({
        key: `${report.id}:${def.type}`,
        type: def.type,
        category: CATEGORY.get(def.type) as EventCategory,
        at: found,
        reportId: report.id,
        kind: report.kind,
      });
    }
  }
  return events.sort(
    (a, b) =>
      Date.parse(a.at) - Date.parse(b.at) ||
      (ORDER.get(a.type) ?? 0) - (ORDER.get(b.type) ?? 0) ||
      a.reportId.localeCompare(b.reportId),
  );
}
